import { useId } from 'react';

import { PersonFaceSource } from 'components/person-avatar';
import { DeletedTask } from 'services/dashboard.service';
import { deletedTitle, deletionReason, formatDayInSaoPaulo } from 'pages/MissaoDestaque/deleted-tasks';

import CabecalhoDaRealizacao from './CabecalhoDaRealizacao';

/** Quem exclui é o próprio cadastrante: o cabeçalho de cada cartão usa os dados do usuário logado. */
export interface AutorDasExcluidas {
  chave: string;
  nomeGuerra?: string;
  rosto: PersonFaceSource;
}

/** Aviso da leitura de `find/entregas_excluidas` que falhou (RDD-13); a seção de canceladas o repete (RDV-34). */
export const FALHA_DAS_EXCLUIDAS = 'Não foi possível carregar as realizações excluídas.';

export function AvisoDeFalhaDasExcluidas() {
  return (
    <p className="m-0 rounded-jj-lg bg-brand-white px-[26px] py-6 text-[13px] text-content shadow-jj-01">{FALHA_DAS_EXCLUIDAS}</p>
  );
}

/** "Excluída por você em dd/mm/aaaa." no fuso de São Paulo, como as demais datas das excluídas; sem data, sem ela. */
function linhaDaExclusao(ms?: number): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return 'Excluída por você.';
  return `Excluída por você em ${formatDayInSaoPaulo(ms)}.`;
}

export interface CartaoDaRealizacaoExcluidaProps {
  realizacao: DeletedTask;
  autor: AutorDasExcluidas;
  rotuloDaFase: string;
}

/**
 * Cartão de "Realizações canceladas" (RDV-22, AD-039): cabeçalho com a pílula "Excluída" e "0" pontos recebidos, data
 * e motivo da exclusão; sem os campos de RDV-19 (`find/entregas_excluidas` não os traz), trilha, ação nem "Mais
 * informações".
 */
export function CartaoDaRealizacaoExcluida({ realizacao, autor, rotuloDaFase }: CartaoDaRealizacaoExcluidaProps) {
  const tituloId = useId();
  const motivo = deletionReason(realizacao);

  return (
    <article
      aria-labelledby={tituloId}
      className="flex flex-col gap-3.5 rounded-jj-lg bg-brand-white px-5 pb-4 pt-[18px] shadow-jj-01"
    >
      <CabecalhoDaRealizacao
        tituloId={tituloId}
        titulo={deletedTitle(realizacao)}
        autor={{ chave: autor.chave, nomeGuerra: autor.nomeGuerra?.trim() ?? '', rosto: autor.rosto }}
        propria
        rotuloDaFase={rotuloDaFase}
        cadastradaEm={realizacao.created}
        pilula="Excluída"
        pontos={0}
      />
      <div className="flex items-center gap-[9px] rounded-jj-md bg-surface-200 px-[13px] py-2.5">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          className="shrink-0 text-content"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
        <p className="m-0 whitespace-pre-line break-words text-xs leading-[1.5] text-content">
          {motivo ? `${linhaDaExclusao(realizacao.deletion?.time)} ${motivo}` : linhaDaExclusao(realizacao.deletion?.time)}
        </p>
      </div>
    </article>
  );
}
